// =======================
// TrialBalanceYTDPDF.js
// =======================
import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";

// =======================
// STYLES
// =======================
const styles = StyleSheet.create({
  page: {
    fontSize: 10,
    fontFamily: "Helvetica",
    flexDirection: "column",
    padding: 10,
  },
  table: { display: "table", width: "auto", marginTop: 5 },
  tableRow: { flexDirection: "row" },
  tableCol: {
    borderStyle: "solid",
    borderWidth: 1,
    borderColor: "#000",
    borderTopWidth: 0,
    borderLeftWidth: 0,
    padding: 4,
    flexWrap: "wrap",
    wordBreak: "break-word",
  },
  tableColHeader: {
    backgroundColor: "#e0e0e0",
    fontWeight: "bold",
    textAlign: "center",
  },
  nodeRow: {
    backgroundColor: "#cce5ff",
    fontWeight: "bold",
    padding: 4,
  },
  groupRow: {
    backgroundColor: "#e6f2ff",
    fontWeight: "bold",
    padding: 4,
  },
  subtotalRow: {
    backgroundColor: "#f0f0f0",
    fontWeight: "bold",
    padding: 4,
  },
  totalRow: {
    backgroundColor: "#b3b3b3",
    fontWeight: "bold",
    padding: 4,
  },
  textLeft: { textAlign: "left" },
  textRight: { textAlign: "right" },
  headerImage: { padding: 2 },
});

// =======================
// HEADER
// =======================
const Header = ({ parentDetails }) => {
  let apiPaths_from_local_storage = localStorage.getItem("apiPaths")
    ? JSON.parse(localStorage.getItem("apiPaths"))
    : [];

  return (
    <View style={{ width: "100%" }} fixed>
      <View style={{ padding: 10 }}>
        {apiPaths_from_local_storage[0]?.REP_HEADER?.includes("data:image/") && (
          <Image
            src={apiPaths_from_local_storage[0].REP_HEADER}
            style={styles.headerImage}
          />
        )}
        <Text style={{ fontSize: 12, fontWeight: "bold", textAlign: "center", marginTop: 10 }}>
          Trial Balance YTD
        </Text>
      </View>
    </View>
  );
};

// =======================
// FOOTER
// =======================
const Footer = () => (
  <View style={{ width: "100%" }} fixed>
    <Text
      style={{ textAlign: "center", fontSize: 10 }}
      render={({ pageNumber, totalPages }) => `Page ${pageNumber} of ${totalPages}`}
    />
  </View>
);

// Normalized case helper function for formatting text alignment
const isNumericColumn = (key) => {
  if (!key) return false;
  const targetKey = String(key).toUpperCase().trim();
  return ["DEBIT", "CREDIT", "BALANCE", "OPENDR", "OPENCR"].includes(targetKey);
};

// Normalized safe data extractor helper function handling casing issues like OPENDr / OPENDR
const getNumericValue = (row, key) => {
  if (!row || !key) return 0;
  const normalizedKey = String(key).toUpperCase().trim();
  
  if (normalizedKey === "OPENDR") return Number(row.OPENDR) || Number(row.OPENDr) || 0;
  if (normalizedKey === "OPENCR") return Number(row.OPENCR) || Number(row.OPENCr) || 0;
  
  return Number(row[key]) || 0;
};

// =======================
// TABLE CONTENT
// =======================
const TableContent = ({ groupedData, columns, colWidths, grandTotal }) => (
  <View style={{ flexGrow: 1 }} wrap>
    {/* Table Header */}
    <View style={styles.tableRow} fixed>
      {columns.map((col, i) => (
        <View
          key={i}
          style={[
            styles.tableCol,
            styles.tableColHeader,
            i === 0 && { borderLeftWidth: 1 },
            { flex: colWidths[i] || 1 },
          ]}
        >
          <Text>{col.column_name}</Text>
        </View>
      ))}
    </View>

    {/* Table Body */}
    {Object.entries(groupedData).map(([node, groups], nodeIndex) => {
      const nodeSubtotal = Object.values(groups)
        .flat()
        .reduce(
          (acc, r) => ({
            OPENDR: acc.OPENDR + getNumericValue(r, "OPENDR"),
            OPENCR: acc.OPENCR + getNumericValue(r, "OPENCR"),
            DEBIT: acc.DEBIT + getNumericValue(r, "DEBIT"),
            CREDIT: acc.CREDIT + getNumericValue(r, "CREDIT"),
            BALANCE: acc.BALANCE + getNumericValue(r, "BALANCE"),
          }),
          { OPENDR: 0, OPENCR: 0, DEBIT: 0, CREDIT: 0, BALANCE: 0 }
        );

      return (
        <React.Fragment key={nodeIndex}>
          {/* Node Header */}
          <View style={styles.tableRow} wrap={false}>
            <View style={[styles.tableCol, styles.nodeRow, { flex: colWidths.reduce((a, b) => a + b, 0) }]}>
              <Text>{node}</Text>
            </View>
          </View>

          {/* Group Rows */}
          {Object.entries(groups).map(([groupCode, rows], groupIndex) => {
            const groupSubtotal = rows.reduce(
              (acc, r) => ({
                OPENDR: acc.OPENDR + getNumericValue(r, "OPENDR"),
                OPENCR: acc.OPENCR + getNumericValue(r, "OPENCR"),
                DEBIT: acc.DEBIT + getNumericValue(r, "DEBIT"),
                CREDIT: acc.CREDIT + getNumericValue(r, "CREDIT"),
                BALANCE: acc.BALANCE + getNumericValue(r, "BALANCE"),
              }),
              { OPENDR: 0, OPENCR: 0, DEBIT: 0, CREDIT: 0, BALANCE: 0 }
            );

            return (
              <React.Fragment key={groupIndex}>
                {/* Group Header */}
                <View style={styles.tableRow} wrap={false}>
                  <View style={[styles.tableCol, styles.groupRow, { flex: colWidths.reduce((a, b) => a + b, 0) }]}>
                    <Text>{groupCode}</Text>
                  </View>
                </View>

                {/* Data Rows */}
                {rows.map((row, i) => (
                  <View style={styles.tableRow} key={i} wrap={false}>
                    {columns.map((col, ci) => {
                      const key = col.column_key_from_api;
                      const isNum = isNumericColumn(key);
                      const displayVal = isNum ? getNumericValue(row, key) : (row[key] ?? "");
                      return (
                        <View key={ci} style={[styles.tableCol, { flex: colWidths[ci] || 1 }]}>
                          <Text style={isNum ? styles.textRight : styles.textLeft}>
                            {isNum && typeof displayVal === "number" ? displayVal.toFixed(2) : displayVal}
                          </Text>
                        </View>
                      );
                    })}
                  </View>
                ))}

                {/* Group Subtotal Row */}
                <View style={styles.tableRow} wrap={false}>
                  {columns.map((col, ci) => {
                    let value = "";
                    const key = col.column_key_from_api;
                    if (ci === 0) value = "Subtotal";
                    else if (isNumericColumn(key)) {
                      const normalizedKey = String(key).toUpperCase().trim();
                      value = (groupSubtotal[normalizedKey] || 0).toFixed(2);
                    }
                    return (
                      <View key={ci} style={[styles.tableCol, styles.subtotalRow, { flex: colWidths[ci] || 1 }]}>
                        <Text style={isNumericColumn(key) ? styles.textRight : styles.textLeft}>
                          {value}
                        </Text>
                      </View>
                    );
                  })}
                </View>
              </React.Fragment>
            );
          })}

          {/* Node Total Row */}
          <View style={styles.tableRow} wrap={false}>
            {columns.map((col, ci) => {
              const key = col.column_key_from_api;
              const normalizedKey = String(key).toUpperCase().trim();
              return (
                <View key={ci} style={[styles.tableCol, styles.totalRow, { flex: colWidths[ci] || 1 }]}>
                  <Text style={isNumericColumn(key) ? styles.textRight : styles.textLeft}>
                    {ci === 0 ? "Node Total" : isNumericColumn(key) ? (nodeSubtotal[normalizedKey] || 0).toFixed(2) : ""}
                  </Text>
                </View>
              );
            })}
          </View>
        </React.Fragment>
      );
    })}

    {/* Grand Total Row */}
    <View style={styles.tableRow} wrap={false}>
      {columns.map((col, ci) => {
        const key = col.column_key_from_api;
        const normalizedKey = String(key).toUpperCase().trim();
        return (
          <View key={ci} style={[styles.tableCol, styles.totalRow, { flex: colWidths[ci] || 1 }]}>
            <Text style={isNumericColumn(key) ? styles.textRight : styles.textLeft}>
              {ci === 0 ? "Grand Total" : isNumericColumn(key) ? (grandTotal[normalizedKey] || 0).toFixed(2) : ""}
            </Text>
          </View>
        );
      })}
    </View>
  </View>
);

// =======================
// MAIN COMPONENT
// =======================
const TrialBalanceYTDPDF = ({ data, parentDetails, selected_mode_table_structure }) => {
  const reportData = data || [];
  const selectedMode = parentDetails.mode_list_array.find((m) => m.is_selected);

  const columns = selected_mode_table_structure
    .find((item) => item.mode_table_name === selectedMode?.mode_name)
    ?.mode_table_columns.filter(
      (col) => col.column_key_from_api !== "NODE" && col.column_key_from_api !== "GROUP CODE"
    );

  // Layout sizing recalculations:
  // Increased OPENDR/OPENCR from 0.55 to 0.95 for extra padding structure.
  // Balanced down the BALANCE column width mapping from 3.8 to 2.3.
  const colWidths = columns.map((col) => {
    const key = String(col.column_key_from_api).toUpperCase().trim();
    if (key === "OPENDR" || key === "OPENCR") return 1.4; 
    if (key === "BALANCE") return 1.6;                  
    if (key === "DEBIT" || key === "CREDIT") return 1.0;   
    return 1.8;                                           
  });

  const groupedData = reportData.reduce((acc, row) => {
    if (!row.NODE) return acc;
    if (!acc[row.NODE]) acc[row.NODE] = {};
    if (!acc[row.NODE][row["GROUP CODE"]]) acc[row.NODE][row["GROUP CODE"]] = [];
    acc[row.NODE][row["GROUP CODE"]].push(row);
    return acc;
  }, {});

  const grandTotal = reportData.reduce(
    (acc, r) => ({
      OPENDR: acc.OPENDR + getNumericValue(r, "OPENDR"),
      OPENCR: acc.OPENCR + getNumericValue(r, "OPENCR"),
      DEBIT: acc.DEBIT + getNumericValue(r, "DEBIT"),
      CREDIT: acc.CREDIT + getNumericValue(r, "CREDIT"),
      BALANCE: acc.BALANCE + getNumericValue(r, "BALANCE"),
    }),
    { OPENDR: 0, OPENCR: 0, DEBIT: 0, CREDIT: 0, BALANCE: 0 }
  );

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <Header parentDetails={parentDetails} />
        <TableContent groupedData={groupedData} columns={columns} colWidths={colWidths} grandTotal={grandTotal} />
        <Footer />
      </Page>
    </Document>
  );
};

export default TrialBalanceYTDPDF;